import React from 'react';
import { Link } from 'gatsby';
import { cva } from 'class-variance-authority';
import { cnMerge } from '../../../utils/utils';

export const LinkButton = ({
  variant,
  size,
  href,
  title,
  clickable = true,
  label,
  textTransform,
  textAlign,
  borderRadius,
  className,
  external = false,
  followLink = false,
  children,
  trackingFn,
  onClick,
  ...props
}) => {
  const linkButtonVariants = cva(
    'inline-flex items-center ring-offset-background select-none',
    {
      variants: {
        variant: {
          default: 'bg-primary-700 !text-white',
          'primary-filled': 'bg-primary-700 !text-white',
          'primary-outlined':
            'bg-white text-primary-700 border-[1px] border-solid border-primary-700',
          'secondary-filled': 'bg-secondary-600 !text-white',
          'secondary-outlined':
            'bg-white text-secondary-700 border-[1px] border-solid border-secondary-700',
          transparent: 'bg-transparent',
          borderOnly: 'border',
        },
        size: {
          default: 'text-clamp-md px-4 py-2',
          sm: 'text-clamp-sm px-3 py-1',
          md: 'text-clamp-md px-4 py-2',
          lg: 'text-clamp-md px-6 py-3',
          xl: 'text-clamp-h6 px-10 py-4',
          button: 'text-clamp-button px-6 py-[10px]',
        },
        borderRadius: {
          default: 'rounded-[48px]',
          none: 'rounded-none',
          sm: 'rounded-lg',
          lg: 'rounded-[48px]',
        },
        textTransform: {
          default: 'normal-case',
          uppercase: 'uppercase',
          lowercase: 'lowercase',
          capitalize: 'capitalize',
          normal: 'normal-case',
        },
        textAlign: {
          default: 'justify-center',
          left: 'justify-left',
          right: 'justify-right',
        },
        clickable: {
          default: 'global-hover',
          true: 'global-hover',
          false: 'cursor-auto opacity-50 pointer-events-none',
        },
      },
      defaultVariants: {
        variant: 'default',
        size: 'default',
        clickable: 'default',
        borderRadius: 'default',
        textTransform: 'default',
      },
    }
  );

  if (external || href?.includes('://') || href?.indexOf('mailto:') === 0) {
    return (
      <a
        href={href}
        title={title ?? label}
        target="_blank"
        rel={`noopener${followLink ? '' : ' nofollow'}`}
        onClick={(e) => {
          trackingFn(e);
        }}
        className={cnMerge(
          linkButtonVariants({
            variant,
            size,
            clickable,
            textTransform,
            textAlign,
            borderRadius,
          }),
          className
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children ?? label}
      </a>
    );
  }

  return href ? (
    <Link
      to={clickable ? href : '#'}
      title={title ?? label}
      onClick={clickable ? onClick || undefined : (e) => e.preventDefault()}
      className={cnMerge(
        linkButtonVariants({
          variant,
          size,
          clickable,
          textTransform,
          textAlign,
          borderRadius,
        }),
        className
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children ?? label}
    </Link>
  ) : (
    <button
      type="button"
      title={title ?? label}
      disabled={!clickable}
      onClick={clickable ? onClick || undefined : (e) => e.preventDefault()}
      className={cnMerge(
        linkButtonVariants({
          variant,
          size,
          clickable,
          textTransform,
          textAlign,
          borderRadius,
        }),
        className
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children ?? label}
    </button>
  );
};
